import React from 'react';
import PropTypes from 'prop-types';
import Footer from '@ajat/design-system/Footer';
import { ComponentPage, DemoCard, ViewportFrame } from '../shared';
import BRAND_LOGOS from '@ajat/design-system/assets/logos';

export const meta = {
  component: Footer,
  title: 'Footer',
  group: 'Navigation',
  importPath: '@ajat/design-system/Footer',
  description:
    'Site footer with link columns, contact details, social icons and a legal bar. Columns sit side by side on desktop and stack on mobile. Brand pages import their configured wrapper from Clothing/{Brand}/Common rather than this component.',
  props: [
    ['brand', "'CLS' | 'ABC' | 'Overall' | 'Crownstudent'", "'CLS'", 'Brand — selects default columns, contact details, socials and the Overall extras (wordmark, tagline, payment badges)'],
    ['columns', 'array', 'per brand', '[{ title, links: [{ label, href }] }]'],
    ['contact', 'object', 'per brand', '{ title, email, address } — null renders no contact column (the socials take their own)'],
    ['socials', 'array', 'per brand', "[{ icon: 'facebook' | 'instagram' | 'youtube' | image URL, href, ariaLabel }]"],
    ['legalLinks', 'array', 'per brand', '[{ label, href }] rendered in the bottom bar'],
    ['copyrightPlacement', "'bar' | 'mobile-only'", "'bar'", 'Where the copyright shows — mobile-only for a brand whose desktop bar is links + badges (OVER/LL)'],
    ['paymentMethodsLabel', 'string', "'Payment methods'", 'Accessible name of the badge list'],
    ['copyright', 'string', 'per brand', 'Copyright line (Overall shows it on mobile only — Figma 2497:94263 has none on desktop)'],
    ['logo', 'object', '—', '{ src, alt } — large wordmark above the columns (Figma 2497:94227); switches the brand block on, on any brand; omit it and the block is not rendered'],
    ['tagline', 'string', '—', 'One line under the wordmark'],
    ['paymentMethods', 'array', '—', '[{ id, label, icon }] badges in the bottom bar — switches the badges on, on any brand'],
    ['variant', "'full' | 'bar'", "'full'", 'bar = the legal bar alone (links, badges, copyright) — the cart mini-footer (OVER-LL 2762:29846)'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'On a brand page',
      code: `import { Header, Footer } from '../Common';

<Footer />`,
    },
    {
      title: 'Cart mini-footer (legal bar only)',
      code: `// OVER/LL cart (Overall/Cart)
import { Footer, PAYMENT_METHODS } from '../Common';

<Footer variant="bar" paymentMethods={PAYMENT_METHODS} />`,
    },
    {
      title: 'Custom columns',
      code: `<Footer
  brand="CLS"
  columns={[
    {
      title: 'Produkter',
      links: [
        { label: 'Studieturstrøjer', href: '/clothing/cls-dk/shop/studieturstrojer' },
        { label: 'Vogntrøjer', href: '/clothing/cls-dk/shop/vogntrojer' },
      ],
    },
  ]}
  copyright="© 2026 C.L. Seifert"
/>`,
    },
  ],
};

const BRAND_BLOCK = {
  logo: { src: BRAND_LOGOS.Overall.wordmark, alt: 'OVER/LL' },
  tagline: 'OFFICIAL UNIFORM OF UNOFFICIAL BEHAVIOR · DESIGNED IN STOCKHOLM',
  paymentMethods: [
    { id: 'klarna', label: 'Klarna' },
    { id: 'swish', label: 'Swish' },
    { id: 'visa', label: 'Visa' },
    { id: 'mastercard', label: 'Mastercard' },
  ],
};

export default function FooterPage({ brand = 'CLS' }) {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Brand block + payment badges" hint="appearance follows the data — the same props on any brand; the brand key only picks the skin tokens" flush>
        <ViewportFrame width={1440} height={700} label="Footer with brand block at 1440px">
          <Footer brand={brand} {...BRAND_BLOCK} contact={null} copyrightPlacement="mobile-only" />
        </ViewportFrame>
      </DemoCard>

      <DemoCard title="Bar variant" hint='variant="bar" · the legal bar alone — the cart mini-footer' flush>
        <ViewportFrame width={1440} height={120} label="Footer bar variant at 1440px">
          <Footer brand={brand} variant="bar" paymentMethods={BRAND_BLOCK.paymentMethods} />
        </ViewportFrame>
      </DemoCard>

      <DemoCard title="Desktop" hint="≥ 1024px · columns side by side" flush>
        <ViewportFrame width={1440} height={560} label="Footer at 1440px">
          <Footer brand={brand} />
        </ViewportFrame>
      </DemoCard>

      <DemoCard title="Mobile" hint="< 1024px · columns stack" flush>
        <ViewportFrame width={393} height={900} label="Footer at 393px">
          <Footer brand={brand} />
        </ViewportFrame>
      </DemoCard>
    </ComponentPage>
  );
}

FooterPage.propTypes = {
  brand: PropTypes.oneOf(['CLS', 'ABC', 'Overall', 'Crownstudent']),
};
